import { UserContext, CompletionPattern, WorkingHours, ProductivityMetrics, CompletionEvent } from '../../types/intelligentScheduling';
import { List } from '../../types/lists';

/**
 * Context Analysis Service for Intelligent Scheduling
 * 
 * This service analyzes user patterns and context to provide intelligent scheduling recommendations.
 * It integrates with the existing GodMode data structures and progressively learns from user behavior.
 */
export class ContextAnalysisService {
  private completionHistory: CompletionEvent[] = [];

  constructor() {
    this.loadStoredData();
  }

  // === CORE CONTEXT GATHERING ===
  
  /**
   * Gathers comprehensive user context for intelligent scheduling
   */
  async gatherUserContext(): Promise<UserContext> {
    const currentTime = new Date();
    
    return {
      currentLocation: await this.getCurrentLocation(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      workingHours: this.getUserWorkingHours(),
      historicalCompletionData: await this.analyzeCompletionPatterns(),
      currentDateTime: currentTime,
      preferredWorkStyles: this.inferWorkStyles(),
      productivityMetrics: this.calculateProductivityMetrics(),
      environmentalFactors: this.assessEnvironmentalFactors()
    };
  }

  // === LOCATION INTELLIGENCE ===
  
  private async getCurrentLocation(): Promise<{ latitude: number; longitude: number } | undefined> {
    if (!navigator.geolocation) return undefined;
    
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        () => resolve(undefined),
        { enableHighAccuracy: false, timeout: 5000 }
      );
    });
  }

  // === TIME INTELLIGENCE ===
  
  private getUserWorkingHours(): WorkingHours {
    // Default working hours - can be customized later
    const defaultHours = { start: '09:00', end: '17:00', enabled: true };
    const weekendHours = { start: '10:00', end: '16:00', enabled: true };

    return {
      monday: defaultHours,
      tuesday: defaultHours,
      wednesday: defaultHours,
      thursday: defaultHours,
      friday: defaultHours,
      saturday: weekendHours,
      sunday: { ...weekendHours, enabled: false }
    };
  }

  /**
   * Analyzes historical completion data to identify patterns
   */
  async analyzeCompletionPatterns(): Promise<CompletionPattern[]> {
    const events = await this.loadCompletionHistory();
    const patterns: CompletionPattern[] = [];
    
    // Group events by time blocks (morning, afternoon, evening)
    const timeBlocks = this.groupEventsByTimeBlocks(events);
    
    for (const [timeBlock, blockEvents] of timeBlocks) {
      if (blockEvents.length === 0) continue;
      
      const pattern = this.calculatePatternMetrics(timeBlock, blockEvents);
      patterns.push(pattern);
    }
    
    return patterns.sort((a, b) => b.completionRate - a.completionRate);
  }

  private groupEventsByTimeBlocks(events: CompletionEvent[]): Map<string, CompletionEvent[]> {
    const blocks = new Map<string, CompletionEvent[]>();
    
    // Initialize time blocks
    ['morning', 'afternoon', 'evening'].forEach(block => {
      blocks.set(block, []);
    });

    events.forEach(event => {
      const hour = event.completedAt.getHours();
      let timeBlock: string;
      
      if (hour >= 6 && hour < 12) timeBlock = 'morning';
      else if (hour >= 12 && hour < 18) timeBlock = 'afternoon';
      else timeBlock = 'evening';
      
      blocks.get(timeBlock)!.push(event);
    });

    return blocks;
  }

  private calculatePatternMetrics(timeBlock: string, events: CompletionEvent[]): CompletionPattern {
    const totalEvents = events.length;
    const completedEvents = events.filter(e => e.quality >= 3).length; // Quality 3+ = successful
    
    const averageHour = timeBlock === 'morning' ? 9 : timeBlock === 'afternoon' ? 14 : 20;
    const averageDay = Math.round(events.reduce((sum, e) => sum + e.completedAt.getDay(), 0) / totalEvents) || 1;
    
    return {
      timeOfDay: averageHour,
      dayOfWeek: averageDay,
      completionRate: totalEvents > 0 ? completedEvents / totalEvents : 0,
      averageItemsCompleted: this.calculateAverageItemsCompleted(events),
      averageFocusTime: this.calculateAverageFocusTime(events),
      taskTypes: this.extractTaskTypes(events),
      contextualSuccess: {
        locationSuccessRate: this.calculateLocationSuccessRates(events),
        timeBlockSuccessRate: { [timeBlock]: completedEvents / totalEvents },
        deadlinePressureResponse: this.calculateDeadlinePressureResponse(events)
      }
    };
  }

  // === PRODUCTIVITY METRICS ===
  
  private calculateProductivityMetrics(): ProductivityMetrics {
    const events = this.completionHistory;
    
    return {
      averageTaskCompletionTime: this.calculateAverageCompletionTime(events),
      focusSessionLength: this.calculateAverageFocusTime(events),
      optimalTaskSwitchingInterval: 25, // Default Pomodoro-style
      peakProductivityHours: this.identifyPeakHours(events),
      burnoutIndicators: this.assessBurnoutIndicators(events)
    };
  }

  private calculateAverageCompletionTime(events: CompletionEvent[]): number {
    if (events.length === 0) return 30; // Default 30 minutes
    
    const totalTime = events.reduce((sum, event) => sum + event.duration, 0);
    return Math.round(totalTime / events.length);
  }

  private calculateAverageFocusTime(events: CompletionEvent[]): number {
    // Estimate focus time based on completion duration and distraction level
    const focusEvents = events.filter(e => e.context.distractionLevel <= 2);
    if (focusEvents.length === 0) return 25; // Default
    
    const totalFocusTime = focusEvents.reduce((sum, event) => {
      // Adjust duration based on distraction level
      const focusRatio = (6 - event.context.distractionLevel) / 5;
      return sum + (event.duration * focusRatio);
    }, 0);
    
    return Math.round(totalFocusTime / focusEvents.length);
  }

  private identifyPeakHours(events: CompletionEvent[]): number[] {
    const hourlyPerformance: { [hour: number]: { count: number; avgQuality: number } } = {};
    
    // Initialize all hours
    for (let hour = 0; hour < 24; hour++) {
      hourlyPerformance[hour] = { count: 0, avgQuality: 0 };
    }
    
    // Calculate performance by hour
    events.forEach(event => {
      const hour = event.completedAt.getHours();
      const perf = hourlyPerformance[hour];
      perf.avgQuality = (perf.avgQuality * perf.count + event.quality) / (perf.count + 1);
      perf.count++;
    });
    
    // Find top performing hours (quality >= 4 and sufficient data)
    const peakHours = Object.entries(hourlyPerformance)
      .filter(([_, perf]) => perf.count >= 3 && perf.avgQuality >= 4)
      .sort(([_, a], [__, b]) => b.avgQuality - a.avgQuality)
      .slice(0, 4) // Top 4 hours
      .map(([hour]) => parseInt(hour));
    
    return peakHours.length > 0 ? peakHours : [9, 10, 14, 15]; // Default peak hours
  }

  private assessBurnoutIndicators(events: CompletionEvent[]): any[] {
    // Simple burnout detection based on recent patterns
    const recentEvents = events.filter(e => {
      const daysSince = (Date.now() - e.completedAt.getTime()) / (1000 * 60 * 60 * 24);
      return daysSince <= 7; // Last week
    });
    
    const indicators = [];
    
    // Check for declining quality
    if (recentEvents.length >= 5) {
      const avgQuality = recentEvents.reduce((sum, e) => sum + e.quality, 0) / recentEvents.length;
      if (avgQuality < 3) {
        indicators.push({
          type: 'declining_quality',
          severity: avgQuality < 2 ? 'high' : 'medium',
          recommendation: 'Consider taking breaks and reducing workload'
        });
      }
    }
    
    return indicators;
  }

  // === WORK STYLE INFERENCE ===
  
  private inferWorkStyles(): ('focused' | 'distributed' | 'morning' | 'evening')[] {
    const events = this.completionHistory;
    const styles: ('focused' | 'distributed' | 'morning' | 'evening')[] = [];
    
    // Analyze time preferences
    const morningEvents = events.filter(e => e.completedAt.getHours() < 12);
    const eveningEvents = events.filter(e => e.completedAt.getHours() >= 18);
    
    if (morningEvents.length > eveningEvents.length) {
      styles.push('morning');
    } else if (eveningEvents.length > morningEvents.length) {
      styles.push('evening');
    }
    
    // Analyze focus patterns
    const avgFocusTime = this.calculateAverageFocusTime(events);
    if (avgFocusTime > 45) {
      styles.push('focused');
    } else {
      styles.push('distributed');
    }
    
    return styles;
  }

  // === ENVIRONMENTAL FACTORS ===
  
  private assessEnvironmentalFactors(): any {
    // Basic environmental assessment - can be enhanced with user input
    return {
      weatherSensitivity: false,
      noiseToleranceLevel: 'medium' as const,
      lightingPreference: 'natural' as const,
      socialInteractionImpact: 'neutral' as const
    };
  }

  // === DATA MANAGEMENT ===
  
  private loadStoredData(): void {
    try {
      const stored = localStorage.getItem('godmode_completion_events');
      if (stored) {
        this.completionHistory = JSON.parse(stored).map((event: any) => ({
          ...event,
          completedAt: new Date(event.completedAt)
        }));
      }
    } catch (error) {
      console.error('Failed to load completion history:', error);
      this.completionHistory = [];
    }
  }

  private async loadCompletionHistory(): Promise<CompletionEvent[]> {
    return this.completionHistory;
  }

  /**
   * Records a completion event for learning
   */
  recordCompletionEvent(event: CompletionEvent): void {
    this.completionHistory.push(event);
    this.saveCompletionHistory();
  }

  private saveCompletionHistory(): void {
    try {
      localStorage.setItem('godmode_completion_events', JSON.stringify(this.completionHistory));
    } catch (error) {
      console.error('Failed to save completion history:', error);
    }
  }

  // === TASK ANALYSIS ===
  
  /**
   * Analyzes a task/list to determine optimal scheduling parameters
   */
  analyzeTaskComplexity(list: List): {
    complexityScore: number;
    estimatedDuration: number;
    focusRequirement: 'low' | 'medium' | 'high';
    recommendedTimeOfDay: number[];
  } {
    const itemCount = list.items?.length || 1;
    const hasDeadline = !!list.deadline;
    const priority = list.priority || 3;
    
    // Calculate complexity (0-1 scale)
    let complexity = 0;
    complexity += Math.min(itemCount / 10, 0.4); // Item count factor
    complexity += (5 - priority) / 10; // Priority factor (higher priority = more complex)
    complexity += hasDeadline ? 0.2 : 0; // Deadline pressure
    
    const estimatedDuration = Math.max(15, itemCount * 10); // At least 15 minutes
    
    let focusRequirement: 'low' | 'medium' | 'high' = 'medium';
    if (complexity < 0.3) focusRequirement = 'low';
    else if (complexity > 0.7) focusRequirement = 'high';
    
    // Recommend time of day based on focus requirement and user patterns
    const peakHours = this.identifyPeakHours(this.completionHistory);
    const recommendedTimeOfDay = focusRequirement === 'high' 
      ? peakHours 
      : [10, 11, 14, 15, 16]; // More flexible for low/medium focus tasks
    
    return {
      complexityScore: complexity,
      estimatedDuration,
      focusRequirement,
      recommendedTimeOfDay
    };
  }

  // === HELPER METHODS ===
  
  private calculateAverageItemsCompleted(events: CompletionEvent[]): number {
    // This would need integration with actual list completion data
    // For now, return a reasonable default
    return events.length > 0 ? 3 : 1;
  }

  private extractTaskTypes(_events: CompletionEvent[]): string[] {
    // Extract task categories from list data
    // This would integrate with the actual list categorization
    return ['productivity', 'health', 'learning'];
  }

  private calculateLocationSuccessRates(events: CompletionEvent[]): { [location: string]: number } {
    const locations: { [key: string]: { total: number; successful: number } } = {};
    
    events.forEach(event => {
      const location = event.context.location ? 'tracked' : 'unknown';
      if (!locations[location]) {
        locations[location] = { total: 0, successful: 0 };
      }
      locations[location].total++;
      if (event.quality >= 3) {
        locations[location].successful++;
      }
    });
    
    const rates: { [location: string]: number } = {};
    Object.entries(locations).forEach(([location, data]) => {
      rates[location] = data.total > 0 ? data.successful / data.total : 0;
    });
    
    return rates;
  }

  private calculateDeadlinePressureResponse(_events: CompletionEvent[]): { [pressure: string]: number } {
    // Simplified deadline pressure analysis
    return {
      low: 0.8,    // High success rate with low pressure
      medium: 0.7, // Moderate success rate with medium pressure
      high: 0.6    // Lower success rate with high pressure
    };
  }

  // === PUBLIC API ===
  
  /**
   * Gets analytics about user patterns and productivity
   */
  getProductivityAnalytics(): {
    totalCompletions: number;
    averageQuality: number;
    peakHours: number[];
    preferredWorkStyle: string[];
    completionTrend: 'improving' | 'stable' | 'declining';
  } {
    const events = this.completionHistory;

    const averageQuality = events.length > 0 
      ? events.reduce((sum, e) => sum + e.quality, 0) / events.length 
      : 0;

    // Simple trend calculation
    const firstHalf = events.slice(0, Math.floor(events.length / 2));
    const secondHalf = events.slice(Math.floor(events.length / 2));
    
    const firstAvg = firstHalf.length > 0 
      ? firstHalf.reduce((sum, e) => sum + e.quality, 0) / firstHalf.length 
      : 0;
    const secondAvg = secondHalf.length > 0 
      ? secondHalf.reduce((sum, e) => sum + e.quality, 0) / secondHalf.length 
      : 0;

    let trend: 'improving' | 'stable' | 'declining' = 'stable';
    if (secondAvg > firstAvg + 0.3) trend = 'improving';
    else if (secondAvg < firstAvg - 0.3) trend = 'declining';

    return {
      totalCompletions: events.length,
      averageQuality: Math.round(averageQuality * 10) / 10,
      peakHours: this.identifyPeakHours(events),
      preferredWorkStyle: this.inferWorkStyles(),
      completionTrend: trend
    };
  }
}

// Singleton instance
export const contextAnalysisService = new ContextAnalysisService();